/**
 * Logger Utility Module
 * 
 * A configurable logging utility for the SoberLivingSupport application that
 * provides consistent formatting and component identification in logs.
 * Logging can be enabled/disabled globally via the REACT_APP_ENABLE_LOGGING
 * environment variable.
 * 
 * This utility helps with debugging and monitoring application behavior while
 * allowing for easy disabling of logs in production environments.
 * 
 * @module logger
 */

/**
 * Configuration options for creating a Logger instance
 * 
 * @interface LoggerOptions
 * @property {boolean} enabled - Whether logging is enabled for this logger instance
 * @property {string} [prefix] - Optional prefix to identify the source of logs (e.g., component name)
 */
interface LoggerOptions {
  enabled: boolean;
  prefix?: string;
}

/**
 * Logger class that provides methods for different log levels
 * 
 * This class handles formatting and conditional output of logs based on
 * the enabled state. Each log is prefixed with the component name for
 * easy identification of the log source.
 */
class Logger {
  /** Whether logging is enabled for this instance */
  private enabled: boolean;
  
  /** Prefix to identify the source of logs */
  private prefix: string;

  /**
   * Creates a new Logger instance
   * 
   * @param {LoggerOptions} options - Configuration options for the logger
   */
  constructor(options: LoggerOptions) {
    this.enabled = options.enabled;
    this.prefix = options.prefix || 'SoberLivingSupport';
  }

  /**
   * Log information messages
   * 
   * Use for general application information, successful operations,
   * and other non-critical information that might be useful for debugging.
   * 
   * @param {...any[]} args - Arguments to log
   * @returns {void}
   */
  info(...args: any[]): void {
    if (this.enabled) {
      console.log(`[${this.prefix}][INFO]`, ...args);
    }
  }

  /**
   * Log warning messages
   * 
   * Use for potentially problematic situations, deprecated features,
   * or conditions that might lead to errors but don't prevent the application
   * from functioning.
   * 
   * @param {...any[]} args - Arguments to log
   * @returns {void}
   */
  warn(...args: any[]): void {
    if (this.enabled) {
      console.warn(`[${this.prefix}][WARN]`, ...args);
    }
  }

  /**
   * Log error messages
   * 
   * Use for runtime errors, exceptions, failed operations, and other
   * critical issues that affect application functionality.
   * 
   * @param {...any[]} args - Arguments to log
   * @returns {void}
   */
  error(...args: any[]): void {
    if (this.enabled) {
      console.error(`[${this.prefix}][ERROR]`, ...args);
    }
  }

  /**
   * Log debug messages
   * 
   * Use for detailed debugging information that is typically only needed
   * during development or troubleshooting specific issues.
   * 
   * @param {...any[]} args - Arguments to log
   * @returns {void}
   */
  debug(...args: any[]): void {
    if (this.enabled) {
      console.debug(`[${this.prefix}][DEBUG]`, ...args);
    }
  }
}

/**
 * Determine if logging is enabled based on the environment variable
 * 
 * This allows for easy enabling/disabling of logs across the entire application
 * by changing a single environment variable.
 */
const isLoggingEnabled = process.env.REACT_APP_ENABLE_LOGGING === 'true';

/**
 * Default logger instance with the application name as prefix
 * 
 * This can be imported directly for general application logging
 * when a specific component context isn't needed.
 */
const logger = new Logger({
  enabled: isLoggingEnabled,
});

/**
 * Factory function to create component-specific logger instances
 * 
 * This allows components to create their own logger instances with a specific
 * prefix, making it easy to identify which component generated a log message.
 * 
 * @example
 * // In a component file:
 * const logger = createLogger('UserProfile');
 * logger.info('Profile loaded successfully');
 * // Output: [UserProfile][INFO] Profile loaded successfully
 * 
 * @param {string} prefix - The prefix to identify the source of logs (typically component name)
 * @returns {Logger} A new Logger instance with the specified prefix
 */
export const createLogger = (prefix: string): Logger => {
  return new Logger({
    enabled: isLoggingEnabled,
    prefix,
  });
};

export default logger;
